import {
    getCountryByCountryCode,
    getCountryByGeoIpCode,
    getCountryByBrowserLanguage
} from "../helpers/CountrySelector";
import { NetworkInstance } from "../../../networkinstances/impl/model/NetworkInstance";
import { CountryCodes } from "./../enums/CountryCodes";
import { CountrySorters } from "./../helpers/CountrySorters";
import { Country } from "./../model/Country";
import { getLogger } from "@goodgamestudios/logger";

const logger = getLogger("WorldAssignment.CountriesManager");

export class CountriesManager {
    public detectionFactor: number = 0;
    public currentCountry: Country;
    public defaultCountry: Country;
    public allCountries: Country[] = [];
    public activeCountries: Country[] = [];
    public notSupportedCountries: Country[] = [];

    public initCountries(countries: Country[]) {
        let defaultFallbackCountry: Country;

        this.allCountries = countries;

        for (let i: number = 0; i < this.allCountries.length; i++) {
            let tempAbstractGGSCountry: Country = this.allCountries[i];

            // Take "XX", international, as fallback country for the case that no country in the
            // country.xml is marked as "default"
            if (tempAbstractGGSCountry.ggsCountryCode.toLowerCase() === CountryCodes.INTERNATIONAL.toLowerCase()) {
                defaultFallbackCountry = tempAbstractGGSCountry;
            }

            // Which country is used as "default" is defined in the country.xml with the "default=1" XML attribute.
            // It may be possible that another country than "XX" is configured as the default.
            if (tempAbstractGGSCountry.isDefault) {
                this.defaultCountry = tempAbstractGGSCountry;
                break;
            }
        }

        if (!this.defaultCountry) {
            logger.warn("There is no default country specified in the country.xml, using 'XX' as default instead. Check country.xml configuration!");
            this.defaultCountry = defaultFallbackCountry;
        }
    }

    public initActiveCountries(instances: NetworkInstance[], callback?: Function) {
        for (let i = 0; i < instances.length; i++) {
            this.activeCountries = this.activeCountries.concat(instances[i].countries);
        }
        this.activeCountries = this.removeDuplicatedActiveCountries();
        this.activeCountries.sort(CountrySorters.sortAlphabetically);
        this.initNotSupportedCountries();
        //
        if (callback) {
            callback();
        }
    }

    public getCountryByCountryCode(countryCode: string, alsoDetectUnavailableCountries: boolean): Country {
        return getCountryByCountryCode(alsoDetectUnavailableCountries ? this.allCountries : this.activeCountries, countryCode);
    }

    public getCountryByGeoIpCode(geoIpCode: string, alsoDetectUnavailableCountries: boolean): Country {
        return getCountryByGeoIpCode(alsoDetectUnavailableCountries ? this.allCountries : this.activeCountries, geoIpCode);
    }

    public isCountryWithBrowserLanguageAvailable(browserLang: string, onlyCheckCountriesInAvailableCountriesList: boolean = true): boolean {
        return getCountryByBrowserLanguage(onlyCheckCountriesInAvailableCountriesList ? this.activeCountries : this.allCountries, browserLang) !== undefined;
    }

    private initNotSupportedCountries(): void {
        let len: number = this.allCountries.length;
        let i: number = 0;

        while (i < len) {
            if (this.activeCountries.indexOf(this.allCountries[i]) === -1) {
                // Ignore international (it's always supported)
                if (this.allCountries[i].ggsCountryCode !== CountryCodes.INTERNATIONAL) {
                    this.notSupportedCountries.push(this.allCountries[i]);
                }
            }
            i++;
        }
    }

    private removeDuplicatedActiveCountries(): Country[] {
        let resultNoDuplicates: Country[] = [];
        let len: number = this.activeCountries.length;
        let i: number = 0;

        while (i < len) {
            if (resultNoDuplicates.indexOf(this.activeCountries[i]) === -1) {
                resultNoDuplicates.push(this.activeCountries[i]);
            }
            i++;
        }
        return resultNoDuplicates;
    }
}
